'use client';

import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { Icon } from '@/components/ui/icon';
import { Loading03Icon } from '@/components/ui/icon/glyphs';
import { Link } from '@/i18n/navigation';
import { useAppRouter } from '@/lib/network/use-app-router';
import { buildThesisCanonicalUrl } from '@/lib/share';

interface ThesisRedirectProps {
  marketPubkey: string;
  signature: string;
}

const ESCAPE_HATCH_DELAY_MS = 10_000;

/**
 * Tiny client island that bounces a `/thesis/<sig>` visit to the
 * canonical market URL with the debate tab + thesis param set. Renders
 * the same "verifying" UI as the auth callback so the handoff feels
 * cohesive across short-lived in-between pages.
 *
 * `router.replace` (not `push`) keeps the share URL out of the back
 * button so a back-tap from the market page lands on the previous tab.
 */
export function ThesisRedirect({ marketPubkey, signature }: ThesisRedirectProps): React.ReactNode {
  const router = useAppRouter();
  const t = useTranslations('thesis.share');
  const target = buildThesisCanonicalUrl(marketPubkey, signature);
  const [showEscape, setShowEscape] = useState(false);

  useEffect((): (() => void) => {
    router.replace(target);
    const timer = setTimeout((): void => {
      setShowEscape(true);
    }, ESCAPE_HATCH_DELAY_MS);
    return (): void => {
      clearTimeout(timer);
    };
  }, [router, target]);

  return (
    <main className="flex min-h-dvh flex-col items-center justify-center gap-4">
      <Icon
        icon={Loading03Icon}
        className="size-10 animate-spin text-foreground-secondary motion-reduce:animate-none"
      />
      <p className="text-body text-foreground-secondary">{t('redirecting')}</p>
      {showEscape && (
        <p className="animate-in fade-in-0 duration-slow text-sm text-foreground-secondary">
          {t('taking_too_long')}{' '}
          <Link href="/" className="underline">
            {t('go_home')}
          </Link>
        </p>
      )}
    </main>
  );
}
